import React, { FC, useState } from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import cn from "classnames";
import { useMediaQuery } from "react-responsive";
import { useAppDispatch } from "src/hooks/redux";
import {
  changeInquiryStatusAsync,
  deleteInquiryAsync,
} from "src/redux/myInquiries/actions";
import { Link } from "src/components/Link";
import { ViewersLabel } from "src/components/Card/ViewersLabel";
import { AddToWatchlist } from "src/components/Card/AddToFavoriteButton/AddToWatchlist";
import { CardPrice } from "src/components/Card/CardPrice";
import { Property } from "src/components/Properties/Property";
import { getImageUrl } from "src/utils/getImageUrl";
import { combineSlugId } from "src/utils/combineSlugId";
import { getItemPath } from "src/utils/getItemPath";
import { PATHNAMES } from "src/constants/routes";
import { SCREEN_BREAKPOINTS } from "src/constants/screenBreakpoints";
import savingsIcon from "src/assets/icons/savings.svg";
import { IInquiry } from "src/@types/inquiries";
import { CloneStatus } from "src/@types/clones";
import { ActionButtons } from "./ActionButtons";
import { inquiryCardTrns } from "./trns";
import { Show } from "../PrivateRoute/Show";
import { ONLY_FOR } from "../PrivateRoute/types";
import { TextEditorView } from "../TextEditor/TextEditorView";

const CURRENCY = "$";

interface Props {
  inquiry?: IInquiry;
  isReadOnly: boolean;
}

export const InquiryCard: FC<Props> = ({ isReadOnly, inquiry }) => {
  const { t } = useTranslation();
  const isMDScreenBreakpoint = useMediaQuery({
    maxWidth: SCREEN_BREAKPOINTS.MD,
  });

  const navigate = useNavigate();

  const {
    title,
    views_count,
    additional_information,
    host,
    slug,
    inquiry_cost,
    investment_for_start,
    saved_count,
    id,
    isFavorite,
    status,
  } = inquiry || {};

  const [isFavoriteInquiry, setIsFavoriteInquiry] = useState(isFavorite);
  const [isLoading, setIsLoading] = useState(false);

  const dispatch = useAppDispatch();

  const { location } = host || {};

  const combinedSlug = combineSlugId(slug, id);

  const inquiryPath = getItemPath(PATHNAMES.INQUIRY_ITEM, {
    slug: combinedSlug,
  });
  const inquiryEditPath = getItemPath(PATHNAMES.EDIT_INQUIRY, {
    slug: combinedSlug,
  });

  const isPaused = status === CloneStatus.PAUSE;

  const onEdit = () => navigate(inquiryEditPath);

  const onDelete = () => dispatch(deleteInquiryAsync(id));

  const onPause = async () => {
    setIsLoading(true);
    await dispatch(changeInquiryStatusAsync({ id, status: CloneStatus.PAUSE }));
    setIsLoading(false);
  };

  const onRestore = async () => {
    setIsLoading(true);
    await dispatch(
      changeInquiryStatusAsync({ id, status: CloneStatus.ACTIVE })
    );
    setIsLoading(false);
  };

  return (
    <div
      className={cn(
        "flex flex-col gap-4 p-5 border border-black-base/20 rounded-20",
        {
          "bg-purple-light": isPaused,
        }
      )}
    >
      <div className="flex justify-between items-center gap-8">
        <Link href={inquiryPath}>
          <h4
            className="break-all line-clamp-1 lg:line-clamp-2 hover:text-purple-base"
            title={title}
          >
            {title}
          </h4>
        </Link>
        <span className="flex items-center justify-center gap-5">
          <ViewersLabel viewsCount={views_count} />

          <Show onlyFor={ONLY_FOR.AUTHORIZED}>
            <AddToWatchlist
              id={id}
              saved_count={saved_count}
              isFavoriteInquiry={isFavoriteInquiry}
              setIsFavorite={setIsFavoriteInquiry}
            />
          </Show>
        </span>
      </div>

      <TextEditorView viewType="text" className="!line-clamp-2">
        {additional_information}
      </TextEditorView>
      <hr />

      {!isReadOnly ? (
        <ActionButtons
          isPaused={isPaused}
          onDelete={onDelete}
          onPause={onPause}
          onRestore={onRestore}
          onEdit={onEdit}
          isLoading={isLoading}
        />
      ) : (
        <div className="flex flex-col md:flex-row justify-between md:items-center gap-1">
          {isMDScreenBreakpoint && (
            <>
              <Property
                className="gap-2.5 !py-0"
                icon={{
                  url: savingsIcon,
                }}
                label={t(inquiryCardTrns.minInvLabel)}
                value={t(inquiryCardTrns.minInvFrom, {
                  currency: CURRENCY,
                  price: investment_for_start,
                })}
              />
              <div className="flex justify-between">
                <CardPrice price={inquiry_cost} />
                <img
                  src={getImageUrl(location?.country?.icon)}
                  alt={location?.country?.slug}
                  className="max-w-12 max-h-8"
                />
              </div>
            </>
          )}
          {!isMDScreenBreakpoint && (
            <>
              <CardPrice price={inquiry_cost} />
              <Property
                className="gap-2.5 !py-0"
                icon={{
                  url: savingsIcon,
                }}
                label={t(inquiryCardTrns.minInvLabel)}
                value={t(inquiryCardTrns.minInvFrom, {
                  currency: CURRENCY,
                  price: investment_for_start,
                })}
              />

              <img
                src={getImageUrl(location?.country?.icon)}
                alt={location?.country?.slug}
                title={location?.country?.title}
                className="max-w-12 max-h-8"
              />
            </>
          )}
        </div>
      )}
    </div>
  );
};
